import React, { useState, useEffect, useRef } from 'react';

const PartnersTrustSection = ({
  title = 'Why partners trust Yomuma Likessa',
  subtitle = 'Trusted by leading organizations across the ecosystem',
  logos = [],
  description = "Yomuma and his team don't just coach startups—they build the systems that make coaching work."
}) => {
  const [animationKey, setAnimationKey] = useState(0);
  const sectionRef = useRef(null);
  const wasIntersectingRef = useRef(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !wasIntersectingRef.current) {
            // Only trigger animation when entering viewport (not already visible)
            setAnimationKey(prev => prev + 1);
            wasIntersectingRef.current = true;
          } else if (!entry.isIntersecting) {
            // Reset flag when leaving viewport to allow re-animation
            wasIntersectingRef.current = false;
          }
        });
      },
      {
        threshold: 0.1, // Trigger when 10% of the section is visible
        rootMargin: '0px 0px -50px 0px' // Trigger slightly before it's fully in view
      }
    );

    const currentRef = sectionRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
      observer.disconnect();
    };
  }, []);
  // Default logos from the Logo folder
  // Files in public folder are served from root, so use /Logo/ path
  const defaultLogos = [
    { name: 'Creative DNA', src: '/Logo/Creative_DNA.png' },
    { name: 'MCF', src: '/Logo/MCF_Logo.jpg' },
    { name: 'RFC', src: '/Logo/RFC.jpeg' },
    { name: 'Creative Hub Ethiopia', src: '/Logo/Creative-Hub-Ethiopia.png' },
    { name: 'Stream Accelerator', src: '/Logo/Stream_Accelerator.png' },
    { name: 'xHub Addis', src: '/Logo/xhub_addis_logo.jpeg' },
    { name: 'BIC Ethiopia', src: '/Logo/BIC_Ethiopia.png' },
    { name: 'IKEA Foundation', src: '/Logo/IKEA_Foundation_Logo.png' },
    { name: 'WIDU', src: '/Logo/WIDU_logo.png' },
    { name: 'Habesha Creative Lab', src: '/Logo/HabeshaCreativeLab.png' },
    { name: 'Ice Addis', src: '/Logo/Ice_Addis_logo.png' },
    { name: 'Venture Meda', src: '/Logo/Venture_Meda_Logo.jpg' },
    { name: 'World Bank', src: '/Logo/World-Bank-Logo.jpg' },
    { name: 'UNDP', src: '/Logo/UNDP-logo.png' },
    { name: 'KOICA', src: '/Logo/KOICA.jpg' },
    { name: 'Ink', src: '/Logo/ink.jpg' }
  ];

  const displayLogos = logos.length > 0 ? logos : defaultLogos;

  return (
    <section ref={sectionRef} className="relative py-16 lg:py-20 w-full overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-[#f8fafc] via-white to-[#e2e8f0]"></div>
      <div className="absolute -top-24 -left-24 h-72 w-72 rounded-full bg-accent-yellow/10 blur-3xl"></div>
      <div className="absolute top-32 -right-32 h-80 w-80 rounded-full bg-blue-300/10 blur-3xl"></div>

      <div className="w-full max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center mb-12 lg:mb-16">
          <span className="inline-flex items-center px-4 py-1 mb-4 text-sm font-semibold text-blue-600 bg-blue-50 rounded-full">
            Trusted Partnerships
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
            {title}
          </h2>
          {subtitle ? (
            <p className="text-gray-600 text-base sm:text-lg max-w-3xl mx-auto">
              {subtitle}
            </p>
          ) : null}
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 sm:gap-6 md:gap-8 items-center justify-items-center">
          {displayLogos.map((logo, index) => {
            // Create varied card styles for visual interest
            const cardVariants = [
              'rounded-2xl', // Standard rounded
              'rounded-xl', // Slightly less rounded
              'rounded-3xl', // More rounded
              'rounded-lg', // Less rounded
              'rounded-2xl' // Back to standard
            ];
            
            const sizeVariants = [
              'max-w-[160px]', // Standard
              'max-w-[180px]', // Slightly larger
              'max-w-[150px]', // Slightly smaller
              'max-w-[170px]', // Medium
              'max-w-[160px]' // Standard
            ];

            const variantIndex = index % cardVariants.length;
            const sizeIndex = index % sizeVariants.length;

            return (
              <div
                key={`${animationKey}-${logo.name || index}`}
                className={`w-full ${sizeVariants[sizeIndex]} flex items-center justify-center group fade-in-up`}
                style={{
                  animationDelay: `${index * 0.1}s`
                }}
              >
                <div className={`w-full ${cardVariants[variantIndex]} bg-white shadow-md border-2 border-slate-200/60 px-4 sm:px-6 py-4 sm:py-5 flex items-center justify-center transition-all duration-500 hover:-translate-y-3 hover:shadow-2xl hover:shadow-accent-yellow/30 hover:border-accent-yellow/60 hover:scale-105 relative overflow-hidden`}>
                  {/* Decorative gradient overlay on hover */}
                  <div className="absolute inset-0 bg-gradient-to-br from-accent-yellow/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                  
                  {/* Logo */}
                  <img
                    src={logo.src}
                    alt={logo.name || 'Trusted partner logo'}
                    className="relative z-10 w-full h-auto object-contain opacity-85 transition-all duration-500 group-hover:opacity-100 group-hover:scale-110"
                    loading="lazy"
                    onError={(e) => {
                      const fullUrl = window.location.origin + logo.src;
                      console.error(`Failed to load logo: ${logo.src}`);
                      console.error(`Full URL attempted: ${fullUrl}`);
                      console.error(`Try accessing directly in browser: ${fullUrl}`);
                      // Show a placeholder or hide the card
                      e.target.style.display = 'none';
                      const card = e.target.closest('.group');
                      if (card) {
                        card.style.opacity = '0.3';
                      }
                    }}
                    onLoad={() => {
                      console.log(`✓ Successfully loaded: ${logo.src}`);
                    }}
                  />
                  
                  {/* Subtle shine effect on hover */}
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
                </div>
              </div>
            );
          })}
        </div>

        {description ? (
          <p className="text-gray-700 text-base sm:text-lg leading-relaxed text-center mt-12 max-w-3xl mx-auto">
            {description}
          </p>
        ) : null}
      </div>
    </section>
  );
};

export default PartnersTrustSection;

